import React from 'react';
import {
  Button,
  Checkbox,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  RadioGroup,
  RadioGroupItem,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import { PiGearThin } from 'react-icons/pi';
import { S57_LAYERS, S57LayerId } from './s57Presets';
import { useS57Config } from './useS57Config';

type Mode = 'all' | 'custom';

export const S57ConfigPanel = () => {
  const { config, updateConfig } = useS57Config();

  const mode: Mode = config.selectedLayers.length === 0 ? 'all' : 'custom';

  const handleModeChange = (next: Mode) => {
    if (next === 'all') {
      updateConfig({ selectedLayers: [] });
    } else {
      // Default to navigation when switching to custom
      updateConfig({ selectedLayers: ['navigation'] });
    }
  };

  const toggleLayer = (layerId: S57LayerId) => {
    const current = config.selectedLayers;
    const next = current.includes(layerId)
      ? current.filter((id) => id !== layerId)
      : [...current, layerId];

    // Prevent deselecting everything — at least one must remain
    if (next.length === 0) return;

    updateConfig({ selectedLayers: next });
  };

  return (
    <Popover>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button
              size="icon"
              variant="ghost"
              className="size-5 flex-shrink-0"
              onClick={(e) => e.stopPropagation()}
            >
              <PiGearThin className="size-3.5" />
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent>Configure S-57 layers</TooltipContent>
      </Tooltip>

      <PopoverContent
        className="w-72 p-0"
        side="right"
        align="start"
        onClick={(e) => e.stopPropagation()}
      >
        <h4 className="sticky top-0 z-10 bg-popover px-4 pt-4 pb-2 text-sm font-semibold">
          S-57 Chart Layers
        </h4>
        <div className="px-4 pb-4 space-y-3">
          <RadioGroup
            value={mode}
            onValueChange={(v) => handleModeChange(v as Mode)}
          >
            {/* All option */}
            <div className="flex items-center gap-2 rounded px-2 py-1.5 hover:bg-muted transition-colors">
              <RadioGroupItem value="all" id="s57-mode-all" />
              <div className="flex-1 min-w-0">
                <Label
                  htmlFor="s57-mode-all"
                  className="cursor-pointer text-sm font-medium"
                >
                  All Layers
                </Label>
                <p className="text-xs text-muted-foreground">
                  Full S-57 chart with all objects
                </p>
              </div>
            </div>

            {/* Custom option */}
            <div className="flex items-center gap-2 rounded px-2 py-1.5 hover:bg-muted transition-colors">
              <RadioGroupItem value="custom" id="s57-mode-custom" />
              <div className="flex-1 min-w-0">
                <Label
                  htmlFor="s57-mode-custom"
                  className="cursor-pointer text-sm font-medium"
                >
                  Custom
                </Label>
                <p className="text-xs text-muted-foreground">
                  Choose which layers to display
                </p>
              </div>
            </div>
          </RadioGroup>

          {/* Layer checkboxes — only visible in custom mode */}
          {mode === 'custom' && (
            <div className="space-y-1 border-t pt-3">
              {S57_LAYERS.map((layer) => (
                <div
                  key={layer.id}
                  className="flex items-center gap-2 cursor-pointer rounded px-2 py-1.5 hover:bg-muted transition-colors"
                >
                  <Checkbox
                    id={`s57-${layer.id}`}
                    checked={config.selectedLayers.includes(layer.id)}
                    onCheckedChange={() => toggleLayer(layer.id)}
                  />
                  <div className="flex-1 min-w-0">
                    <Label
                      htmlFor={`s57-${layer.id}`}
                      className="cursor-pointer text-sm font-medium"
                    >
                      {layer.label}
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      {layer.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
